import { MedicalRecordsViewingLinkResponseDto } from 'common/api';
import React from 'react';
import * as ReactQuery from 'react-query';
import { MutateOptions } from 'react-query';
import { InferMutationOptions } from 'services';
import { GET_REFERRAL_ACTIVITY_KEY } from '../get-referral-activity/hook';
import { Store } from 'store';
import { useIntl } from 'react-intl';
import { ErrorType } from 'utils/http';

import { medicalRecordsViewingLinkAPI } from './api';

type ViewingLinkParams = Parameters<typeof medicalRecordsViewingLinkAPI>[0];
type ViewingLinkCallOptions = MutateOptions<
  MedicalRecordsViewingLinkResponseDto,
  ErrorType,
  ViewingLinkParams
>;

export function useMedicalRecordsViewingLink(
  options?: InferMutationOptions<typeof medicalRecordsViewingLinkAPI>
) {
  const queryClient = ReactQuery.useQueryClient();
  const { showNotification } = Store.useNotifications();
  const intl = useIntl();
  const [isLoading, setIsLoading] = React.useState(false);

  const getViewingLink = React.useCallback(
    (
      variables: ViewingLinkParams,
      callOptions?: ViewingLinkCallOptions
    ): void => {
      setIsLoading(true);
      medicalRecordsViewingLinkAPI(variables)
        .then((data) => {
          void queryClient.invalidateQueries([
            GET_REFERRAL_ACTIVITY_KEY,
            variables.referralId,
          ]);
          options?.onSuccess?.(data, variables, undefined);
          callOptions?.onSuccess?.(data, variables, undefined);
        })
        .catch((error: ErrorType) => {
          void queryClient.invalidateQueries([
            GET_REFERRAL_ACTIVITY_KEY,
            variables.referralId,
          ]);
          showNotification({
            variant: 'error',
            description: getErrorMessage(error.statusCode, intl),
          });
          options?.onError?.(error, variables, undefined);
          callOptions?.onError?.(error, variables, undefined);
        })
        .finally(() => setIsLoading(false));
    },
    [intl, options, queryClient, showNotification]
  );

  return {
    getViewingLink,
    isLoading,
  };
}

function getErrorMessage(
  statusCode: number,
  intl: ReturnType<typeof useIntl>
): string {
  if (statusCode === 409) {
    return intl.formatMessage({
      id: 'is.view_medical_records.records_not_available',
      defaultMessage: 'Records not available.',
    });
  }
  if (statusCode === 503) {
    return intl.formatMessage({
      id: 'is.view_medical_records.try_again',
      defaultMessage: 'Request failed. Please try again.',
    });
  }
  return intl.formatMessage({
    id: 'is.view_medical_records.generic_error',
    defaultMessage: 'Something went wrong. Please try again.',
  });
}
